package com.swetdot.swetgram.ui.picso

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.rememberAsyncImagePainter
import coil3.compose.LocalPlatformContext
import coil3.request.ImageRequest
import coil3.request.CachePolicy
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.design.SwetTextField
import com.swetdot.swetgram.ui.design.usesBrowserNativeTextInput
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

@Composable
fun PicsoCaptureScreen(
    state: PicsoCaptureUiState,
    onSnapPhoto: () -> Unit,
    onCaptionChange: (String) -> Unit,
    onPublish: () -> Unit,
    onBack: () -> Unit,
    onSuccess: () -> Unit,
    modifier: Modifier = Modifier,
) {
    LaunchedEffect(state.isPublished) {
        if (state.isPublished) {
            onSuccess()
        }
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(SwetgramThemeTokens.colors.backgroundDeep)
            .padding(16.dp),
    ) {
        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.SpaceBetween,
        ) {
            // Top Bar
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 16.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .clip(CircleShape)
                        .background(SwetgramThemeTokens.colors.card)
                        .clickable(onClick = onBack),
                    contentAlignment = Alignment.Center,
                ) {
                    SwetIcon(icon = SwetIcons.ChevronLeft, tint = Color.White, contentDescription = "Back", size = 20.dp)
                }

                Text(
                    text = "Новый Picso",
                    color = Color.White,
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold,
                )

                Spacer(modifier = Modifier.size(40.dp))
            }

            // Preview or Viewfinder Placeholder
            if (state.image != null) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth(0.9f)
                            .aspectRatio(1f)
                            .clip(RoundedCornerShape(24.dp))
                            .background(Color(0xFF1E1E1E)),
                        contentAlignment = Alignment.Center,
                    ) {
                        val context = LocalPlatformContext.current
                        val request = remember(state.image) {
                            ImageRequest.Builder(context).data(state.image.bytes)
                                .memoryCachePolicy(CachePolicy.DISABLED)
                                .diskCachePolicy(CachePolicy.DISABLED).build()
                        }
                        val painter = rememberAsyncImagePainter(model = request)
                        Image(
                            painter = painter,
                            contentDescription = "Picso Preview",
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop,
                        )
                    }

                    // Caption Field
                    if (usesBrowserNativeTextInput) {
                        SwetTextField(
                            value = state.caption,
                            onValueChange = onCaptionChange,
                            enabled = !state.isUploading && !state.awaitingConfirmation,
                            placeholder = "Добавить подпись...",
                            modifier = Modifier.fillMaxWidth(0.9f),
                        )
                        Text("Осталось символов: ${state.captionRemaining}", color = Color(0xFF888888), fontSize = 11.sp)
                    } else {
                    OutlinedTextField(
                        value = state.caption,
                        enabled = !state.isUploading && !state.awaitingConfirmation,
                        onValueChange = onCaptionChange,
                        placeholder = { Text("Добавить подпись...", color = Color(0xFF777777)) },
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedTextColor = Color.White,
                            unfocusedTextColor = Color.White,
                            focusedBorderColor = SwetgramThemeTokens.colors.accent,
                            unfocusedBorderColor = Color(0xFF333333),
                        ),
                        singleLine = true,
                        modifier = Modifier.fillMaxWidth(0.9f),
                        shape = RoundedCornerShape(16.dp),
                        supportingText = {
                            Text(
                                text = "Осталось символов: ${state.captionRemaining}",
                                color = Color(0xFF888888),
                                fontSize = 11.sp,
                            )
                        },
                    )
                    }

                    // Error notice
                    state.error?.let { err ->
                        Text(
                            text = err,
                            color = Color(0xFFFF5252),
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Medium,
                        )
                    }

                    // Progress indicator
                    if (state.isUploading) {
                        LinearProgressIndicator(
                            progress = { state.uploadProgress },
                            color = SwetgramThemeTokens.colors.accent,
                            modifier = Modifier.fillMaxWidth(0.9f),
                        )
                    }
                }
            } else {
                // Viewfinder / Prompt
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                ) {
                    Box(
                        modifier = Modifier
                            .size(120.dp)
                            .clip(CircleShape)
                            .background(SwetgramThemeTokens.colors.card)
                            .clickable(onClick = onSnapPhoto),
                        contentAlignment = Alignment.Center,
                    ) {
                        SwetIcon(icon = SwetIcons.ImageIcon, tint = SwetgramThemeTokens.colors.accent, contentDescription = "Camera", size = 48.dp)
                    }

                    Text(
                        text = "Сделать снимок на камеру",
                        color = Color.White,
                        fontSize = 18.sp,
                        fontWeight = FontWeight.Bold,
                    )
                    Text(
                        text = "Фото будет доступно 24 часа в ленте Picso",
                        color = Color(0xFFAAAAAA),
                        fontSize = 13.sp,
                    )
                }
            }

            // Bottom Actions
            if (state.image != null) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(bottom = 24.dp),
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                ) {
                    // Retake
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .height(50.dp)
                            .clip(RoundedCornerShape(25.dp))
                            .background(Color(0xFF262626))
                            .clickable(enabled = !state.isUploading && !state.awaitingConfirmation, onClick = onSnapPhoto),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text("Переснять", color = Color.White, fontWeight = FontWeight.SemiBold, fontSize = 15.sp)
                    }

                    // Publish
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .height(50.dp)
                            .clip(RoundedCornerShape(25.dp))
                            .background(
                                if (state.canPublish) SwetgramThemeTokens.colors.accent else Color(0xFF333333),
                            )
                            .clickable(enabled = state.canPublish, onClick = onPublish),
                        contentAlignment = Alignment.Center,
                    ) {
                        if (state.isUploading) {
                            CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp))
                        } else {
                            Text("Опубликовать", color = Color.White, fontWeight = FontWeight.Bold, fontSize = 15.sp)
                        }
                    }
                }
            } else {
                Spacer(modifier = Modifier.height(50.dp))
            }
        }
    }
}
